'use client';

/**
 * Custom Radio Group Component
 *
 * This is a controlled component that requires parent component management through
 * the "state" prop. The component supports three distinct states:
 *
 * - 'checked'     : Selected state with filled circle
 * - 'unchecked'   : Default unselected state
 * - 'indeterminate': Special state shown with a minus/dash icon (partial or mixed selection)
 */

import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import { Circle, Minus } from 'lucide-react';

import { cn } from '@/lib/utils';

const radioGroupItemVariants = cva(
  'aspect-square h-[14px] w-[14px] rounded-full border-2 border-solid text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  {
    variants: {
      state: {
        checked: null,
        indeterminate: null,
        unchecked: 'border-venn-grey-60',
      },
      disabled: {
        true: 'cursor-not-allowed bg-opacity-50 bg-venn-grey-40 border-venn-grey-40',
        false: null,
      },
    },
    compoundVariants: [
      {
        state: 'checked',
        disabled: false,
        className:
          'border-venn-dark-blue bg-venn-dark-blue hover:bg-venn-deep-light-blue hover:border-venn-deep-light-blue',
      },
      {
        state: 'indeterminate',
        disabled: false,
        class:
          'border-venn-dark-blue bg-venn-dark-blue hover:bg-venn-deep-light-blue hover:border-venn-deep-light-blue',
      },
      {
        state: 'unchecked',
        disabled: false,
        class: 'hover:border-venn-deep-light-blue',
      },
    ],
    defaultVariants: {
      state: 'unchecked',
      disabled: false,
    },
  },
);

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return <RadioGroupPrimitive.Root className={cn('grid gap-[6px]', className)} {...props} ref={ref} />;
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & VariantProps<typeof radioGroupItemVariants>
>(({ className, state, disabled, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(radioGroupItemVariants({ state, disabled }), className)}
      disabled={disabled}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        forceMount={state === 'indeterminate' || state === 'checked' ? true : undefined}
        className="flex items-center justify-center"
      >
        {state === 'indeterminate' ? (
          <Minus className="h-[10px] w-[10px] text-venn-white fill-venn-white" strokeWidth={5} absoluteStrokeWidth />
        ) : (
          <Circle className="h-[6px] w-[6px] text-venn-white fill-venn-white" />
        )}
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
